/* eslint-disable no-nested-ternary */
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Badge
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import MyLoadingButton from '@components/MyLoadingButton';

export default function MyDialog({
  open,
  setOpen,
  title = 'Your Title',
  children,
  onOk,
  onCancel,
  okTitle,
  cancelTitle,
  hasCancelButton,
  isAlert = false,
  hasCloseButton,
  hideDialogButton = false,
  badgeCount,
  sx,
  type,
  subTitle,
  disabled,
  onClose,
  ...rest
}) {
  const { t } = useTranslation(null, { keyPrefix: 'button' });

  const handleClose = () => {
    setOpen(false);
    if (onClose) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          zIndex: 0,
          borderRadius: '16px',
          width: isAlert ? 'auto' : 'unset',
          minWidth: '480px',
          maxWidth: '90vw',

          p: {
            md: isAlert ? '28px 40px' : '30px',
            sm: '30px',
            xs: '15px'
          },
          overflow: 'visible',
          ...sx
        }
      }}
      slotProps={{
        backdrop: { sx: { bgcolor: '#343434', opacity: '0.6 !important' } }
      }}
      {...rest}
    >
      <DialogTitle
        sx={{
          color: '#000',
          fontSize: {
            md: isAlert ? '18px !important' : '25px !important',
            xs: isAlert ? '16px !important' : '18px !important'
          },
          textAlign: isAlert ? 'left' : 'center',
          py: 0,
          px: isAlert ? 0 : 6,
          fontWeight: 700,

          whiteSpace: isAlert ? 'wrap' : 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          mb: isAlert ? 0 : '20px'
        }}
      >
        {typeof title === 'function' ? title() : title}
        {!isAlert && hasCloseButton ? (
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: { md: 22, sm: 18, xs: 18 },
              top: { md: 22, sm: 14, xs: 12 },
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
        {subTitle && typeof subTitle === 'function' ? (
          subTitle
        ) : (
          <Typography
            sx={{ fontSize: 16, color: '#000', fontWeight: 500, mt: 2.5 }}
            className="dialog-subtitle"
          >
            {subTitle}
          </Typography>
        )}
      </DialogTitle>
      {children && (
        <DialogContent sx={{ p: 0, overflowX: 'visible' }}>
          {children}
        </DialogContent>
      )}
      {!hideDialogButton && (
        <DialogActions
          sx={{
            justifyContent: !hasCancelButton ? 'center' : 'flex-end',
            mt: { md: '30px', sm: '15px', xs: '15px' },
            p: 0,
            gap: '10px'
          }}
        >
          {hasCancelButton && (
            <Button
              autoFocus
              onClick={onCancel || handleClose}
              variant="outlined"
              sx={{
                height: { md: '40px', xs: '22px' },
                minWidth: { md: '110px', xs: 'auto' },
                border: '1px solid #E6E6E6 !important',
                boxShadow: 0,
                color: '#BBBBBB',
                fontSize: 12,
                fontWeight: 400,
                ':hover': {
                  boxShadow: 0,
                  backgroundColor: '#EFEFEF',
                  color: '#BBBBBB'
                }
              }}
            >
              {cancelTitle || t('cancel')}
            </Button>
          )}
          <Badge
            badgeContent={badgeCount}
            color="primary"
            invisible={badgeCount === 0}
          >
            <MyLoadingButton
              timeout={onOk ? 500 : 0}
              onClick={onOk || handleClose}
              variant="contained"
              sx={{
                height: { md: '40px', xs: '22px' },
                minWidth: { md: '110px', xs: 'auto' },

                boxShadow: 0,
                border: 0,
                fontSize: 12,
                fontWeight: 400,
                backgroundColor: disabled
                  ? '#CCC'
                  : ['edit', 'register'].includes(type)
                  ? '#0B1293'
                  : '#FE4A4D',
                color: '#FFF',
                ':hover': {
                  border: 0,
                  boxShadow: 0,
                  backgroundColor: ['edit', 'register'].includes(type)
                    ? '#0A0F65'
                    : '#D94949',
                  color: '#FFF'
                }
              }}
              disabled={disabled}
            >
              {okTitle || t('check')}
            </MyLoadingButton>
          </Badge>
        </DialogActions>
      )}
    </Dialog>
  );
}
